/**
 * New Request — submit form.
 *
 * URL: /admin/requests/new?type=<RequestType.code>
 *
 * The form is driven by the RequestType.data_schema returned from the
 * backend — a list of field descriptors. We render `text`, `textarea`,
 * `number`, `date`, and `select` field types. The payload is sent as
 * { request_type, summary, data: {fieldName: value} }.
 */

import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import { extractErr } from '@/components/ui/inline-edit'
import { DatePicker } from '@/components/DatePicker'

type SchemaField = {
  name: string
  label: string
  type: 'text' | 'textarea' | 'number' | 'date' | 'select'
  required?: boolean
  placeholder?: string
  options?: string[]
  default?: string | number
  help?: string
}

type RequestType = {
  id: number
  code: string
  name: string
  description: string
  icon: string
  data_schema: SchemaField[]
  default_workflow: number | null
  is_active: boolean
}

export default function RequestsNew() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const typeCode = searchParams.get('type') || ''

  const typesQ = useQuery({
    queryKey: ['requests', 'types'],
    queryFn: async () => (await api.get<RequestType[]>('/request-types/')).data,
  })

  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null)
  const [summary, setSummary] = useState('')
  const [data, setData] = useState<Record<string, any>>({})

  const types = (typesQ.data || []).filter((t) => t.is_active)

  // Resolve the type from URL ?type= once types load.
  useEffect(() => {
    if (selectedTypeId !== null) return
    if (!types.length) return
    if (typeCode) {
      const t = types.find((x) => x.code === typeCode)
      if (t) {
        setSelectedTypeId(t.id)
        return
      }
    }
    // Fallback to the first active type.
    setSelectedTypeId(types[0].id)
  }, [types, typeCode, selectedTypeId])

  const selectedType = useMemo(
    () => types.find((t) => t.id === selectedTypeId) || null,
    [types, selectedTypeId],
  )

  // Initialize field defaults when type changes.
  useEffect(() => {
    if (!selectedType) return
    const init: Record<string, any> = {}
    for (const f of selectedType.data_schema || []) {
      if (f.default !== undefined) init[f.name] = f.default
    }
    setData(init)
  }, [selectedType])

  const submitMut = useMutation({
    mutationFn: async () => {
      if (!selectedType) throw new Error('Pick a request type.')
      if (!summary.trim()) throw new Error('Summary is required.')
      return (await api.post('/requests/', {
        request_type: selectedType.id,
        summary: summary.trim(),
        data,
      })).data as { id: number }
    },
    onSuccess: (created) => {
      toast.success('Request submitted')
      navigate(`/admin/requests/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  return (
    <AdminLayout>
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/requests" className="text-muted-foreground hover:text-foreground hover:underline">
            ← My Requests
          </Link>
        </div>
        <h2 className="text-2xl font-semibold text-foreground tracking-tight mt-2">New Request</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Pick the type, describe what you need, and your manager (or the configured approver chain) will be notified.
        </p>
      </div>

      <div className="bg-card rounded-lg shadow p-6 max-w-3xl">
        {typesQ.isLoading ? (
          <p className="text-muted-foreground">Loading request types…</p>
        ) : types.length === 0 ? (
          <p className="text-muted-foreground">
            No active request types are configured. An admin must add one in Settings.
          </p>
        ) : (
          <div className="space-y-5">
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1">
                Request type *
              </label>
              <SearchableSelect
                value={selectedTypeId ? String(selectedTypeId) : undefined}
                onChange={(v) => setSelectedTypeId(Number(v))}
                placeholder="— pick a type —"
                options={types.map((t) => ({
                  value: String(t.id),
                  label: `${t.icon ? `${t.icon}  ` : ''}${t.name}`,
                }))}
              />
              {selectedType?.description && (
                <p className="text-xs text-muted-foreground mt-1">{selectedType.description}</p>
              )}
            </div>

            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1">
                Summary *
              </label>
              <Input
                placeholder="One-line title — shown in inbox lists."
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                maxLength={255}
              />
            </div>

            {/* Dynamic schema-driven fields */}
            {(selectedType?.data_schema || []).map((f) => (
              <SchemaFieldInput
                key={f.name}
                field={f}
                value={data[f.name]}
                onChange={(v) => setData((prev) => ({ ...prev, [f.name]: v }))}
              />
            ))}

            <div className="flex items-center gap-3 pt-2">
              <Button
                onClick={() => submitMut.mutate()}
                disabled={!selectedType || submitMut.isPending}
              >
                {submitMut.isPending ? 'Submitting…' : 'Submit request'}
              </Button>
              <Link to="/admin/requests" className="text-sm text-muted-foreground hover:text-foreground hover:underline">
                Cancel
              </Link>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

function SchemaFieldInput({
  field, value, onChange,
}: {
  field: SchemaField
  value: any
  onChange: (v: any) => void
}) {
  const label = (
    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1">
      {field.label}{field.required ? ' *' : ''}
    </label>
  )
  const help = field.help && (
    <p className="text-xs text-muted-foreground mt-1">{field.help}</p>
  )

  if (field.type === 'textarea') {
    return (
      <div>
        {label}
        <Textarea
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          rows={4}
        />
        {help}
      </div>
    )
  }
  if (field.type === 'select') {
    return (
      <div>
        {label}
        <SearchableSelect
          value={value ? String(value) : undefined}
          onChange={onChange}
          placeholder={field.placeholder || '— pick one —'}
          options={(field.options || []).map((opt) => ({ value: opt, label: opt }))}
        />
        {help}
      </div>
    )
  }
  if (field.type === 'number') {
    return (
      <div>
        {label}
        <Input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          placeholder={field.placeholder}
        />
        {help}
      </div>
    )
  }
  if (field.type === 'date') {
    return (
      <div>
        {label}
        <DatePicker
          value={value ?? ''}
          onChange={(v) => onChange(v)}
        />
        {help}
      </div>
    )
  }
  // Default: text
  return (
    <div>
      {label}
      <Input
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
      />
      {help}
    </div>
  )
}
